import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

import { loginSchema } from '@/schemas/auth.schema';
import GoogleIcon from '@components/ui/icons/GoogleIcon';
import { useLogin } from '@/hooks/useLogin';
import FormField from '@/pages/auth/components/FormField';

import './Login.scss';
import AuthLayout from '@/layouts/AuthLayout';
import { Button, Checkbox, Input } from 'antd';

const Login = () => {
  const { loading, googleLoading, requestError, handleEmailLogin, handleGoogleLogin } = useLogin();
  const [showPassword, setShowPassword] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  return (
  <AuthLayout
    leftSideContent={
      <div className="auth-left-slot">
        <p>Don't have an account?</p>
        <Link to="/authentication/register">Get started!</Link>
      </div>
    }
  >
    <h4>Login</h4>
    <div className="login-form-wrap">

      {requestError && (
        <div className="login-error" role="alert">{requestError}</div>
      )}

      <form onSubmit={handleSubmit(handleEmailLogin)} noValidate>

        {/* Email */}
        <FormField label="Email Address" error={errors.email?.message} icon={Mail}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="email"
                placeholder="Email address"
                autoComplete="email"
                className="field-input"
              />
            )}
          />
        </FormField>

        {/* Password */}
        <FormField
          label="Password"
          error={errors.password?.message}
          icon={Lock}
          rightElement={
            <button
              type="button"
              className="field-icon-btn"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          }
        >
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                autoComplete="current-password"
                className="field-input"
              />
            )}
          />
        </FormField>

        {/* Forgot */}
        <div className="login-options">
          <Link to="/authentication/forgot-password" className="forgot-link">
            Forgot Password?
          </Link>
        </div>

        <Button 
          htmlType="submit"
          type="primary" 
          className="login-btn" 
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

      </form>

      <div className="login-divider">
        <span>or continue with</span>
      </div>

      <button
        type="button"
        className="google-btn"
        onClick={handleGoogleLogin}
        disabled={googleLoading}
      >
        <GoogleIcon size={18} />
        {googleLoading ? 'Connecting...' : 'Sign in with Google'}
      </button>

      <div className="login-footer">
        <span className="terms-text">
          By proceeding, you agree to the{' '}
          <Link to="/terms-condition">Terms and Conditions</Link>
        </span>
      </div>

    </div>
    </AuthLayout> 
  );
};

export default Login;

